import { useContext, useMemo } from "react";
import type {
  Airline,
  BookedFlightItineraryWithDepartureTime,
  FlightItinerarySegment,
  MultiTravelItinerary,
  SingleTravelItinerary,
} from "@b2bportal/air-booking-api";
import { TravelItineraryEnum } from "@b2bportal/air-booking-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  ClientName,
  MultiTicketTypeEnum,
  getIsRyanair,
} from "@hopper-b2b/types";
import {
  IconName,
  BannerSeverity,
  NotificationBanner,
  IconComponent,
  Slot,
} from "@hopper-b2b/ui";
import {
  buildRouteString,
  collectSegmentLocatorInfo,
  getAirlineConfirmationNumbers,
  getEnvVariables,
  openExternalLink,
  type SegmentLocatorInfo,
} from "@hopper-b2b/utilities";
import { Typography, Box, Button } from "@material-ui/core";
import clsx from "clsx";

import { ClientContext } from "../../../../../../../../ClientContext";
import { copyTextToClipboardPromise } from "../ConfirmationModalContent/helpers";
import type { CheckInFlightModalContentConnectorProps } from "./container";
import "./styles.scss";

interface ICheckInFlightModalContentProps
  extends CheckInFlightModalContentConnectorProps {
  flight: BookedFlightItineraryWithDepartureTime;
}

export interface IConfirmationNumber {
  label: string;
  locator: string;
  redirectUrl: string;
}

const onCopyAndGo = async (locator: string, redirectUrl: string) => {
  await copyTextToClipboardPromise(locator);
  if (!redirectUrl) return;
  openExternalLink(redirectUrl);
};

/**
 * Gets airline confirmation numbers for check-in (without tenant locator).
 * Note: This exported function uses default English labels.
 * For translated labels, use the hook-based approach inside the component.
 */
export const getConfirmationNumbers = ({
  flight,
  airlineMap,
}: {
  flight: BookedFlightItineraryWithDepartureTime;
  airlineMap: { [key: string]: Airline | undefined };
}): IConfirmationNumber[] => {
  return getAirlineConfirmationNumbers({
    flight,
    airlineMap,
  });
};

/**
 * Builds confirmation numbers with direction-separated labels for Nubank.
 * Produces labels like "Air Canada · Ida: VCP → GIG · Volta: GIG → VCP"
 * instead of the default "Air Canada Ida: VCP → GIG → VCP".
 */
const getNubankConfirmationNumbers = ({
  bookedItinerary,
  airlineMap,
  outboundLabel,
  returnLabel,
}: {
  bookedItinerary: BookedFlightItineraryWithDepartureTime["bookedItinerary"];
  airlineMap: { [key: string]: Airline | undefined };
  outboundLabel: string;
  returnLabel: string;
}): IConfirmationNumber[] => {
  const { travelItinerary } = bookedItinerary;
  const { TravelItinerary } = travelItinerary;
  const allSegmentInfos: SegmentLocatorInfo[] = [];

  const collectSlices = (slices: { segments: unknown[] }[]) => {
    slices.forEach((slice, i) => {
      allSegmentInfos.push(
        ...collectSegmentLocatorInfo({
          segments: slice.segments as FlightItinerarySegment[],
          airlineMap,
          isReturn: i !== 0,
        })
      );
    });
  };

  if (TravelItinerary === TravelItineraryEnum.SingleTravelItinerary) {
    collectSlices((travelItinerary as SingleTravelItinerary).slices);
  } else if (TravelItinerary === TravelItineraryEnum.MultiTravelItinerary) {
    const multi = travelItinerary as MultiTravelItinerary;
    multi.travelItineraries.forEach((it) => collectSlices(it.slices));
  }

  const grouped: Record<string, SegmentLocatorInfo[]> = {};
  allSegmentInfos.forEach((info) => {
    (grouped[info.locator] ??= []).push(info);
  });

  return Object.entries(grouped)
    .filter(([, segments]) => segments.length > 0)
    .map(([locator, segments]) => {
      const { airlineName, redirectUrl } = segments[0];
      const outbound = segments.filter((s) => !s.isReturn);
      const returnSegs = segments.filter((s) => s.isReturn);

      const parts = [airlineName];
      if (outbound.length)
        parts.push(`${outboundLabel}: ${buildRouteString(outbound)}`);
      if (returnSegs.length)
        parts.push(`${returnLabel}: ${buildRouteString(returnSegs)}`);

      return { label: parts.join(" · "), locator, redirectUrl };
    });
};

export const CheckInFlightModalContent = ({
  flight,
  airlineMap,
}: ICheckInFlightModalContentProps) => {
  const { t } = useI18nContext();
  const clientContext = useContext(ClientContext);
  const isRyanair = getIsRyanair(flight);
  const { bookedItinerary } = flight;

  const isNubankClient = getEnvVariables("clientName") === ClientName.NUBANK;

  const confirmationNumbers = useMemo(() => {
    if (isNubankClient) {
      return getNubankConfirmationNumbers({
        bookedItinerary,
        airlineMap,
        outboundLabel: t("outbound"),
        returnLabel: t("returnShort"),
      });
    }

    return getAirlineConfirmationNumbers({
      flight,
      airlineMap,
      labels: {
        confirmationCode: "",
        return: t("return"),
        outbound: t("outbound"),
        originToDestination: (origin: string, destination: string) =>
          t("originToDestination", {
            origin,
            destination,
            interpolation: { escapeValue: false },
          }),
      },
    });
  }, [isNubankClient, flight, bookedItinerary, airlineMap, t]);

  const renderConfirmationNumbers = () => {
    return (
      <Box className="checkin-flight-container">
        {confirmationNumbers.map(({ label, locator, redirectUrl }, index) => (
          <Box
            key={`${locator}-${index}`}
            className={clsx("checkin-flight-details-container", {
              "container-border": index !== confirmationNumbers.length - 1,
            })}
          >
            <Box className="checkin-flight-details">
              <Typography className="checkin-flight-label" variant="body2">
                {label}
              </Typography>
              <Typography variant="body2" className="checkin-flight-locator">
                {locator}
              </Typography>
            </Box>
            {redirectUrl ? (
              <Button
                className="checkin-flight-copy-container"
                onClick={() => onCopyAndGo(locator, redirectUrl)}
                aria-label={t("copyAndGo")}
              >
                <Typography variant="body2" className="copy-text">
                  {t("copyAndGo")}
                </Typography>
                {clientContext?.assets?.externalLink ? (
                  <img
                    src={clientContext?.assets?.externalLink}
                    className="copy-icon"
                    alt=""
                  />
                ) : (
                  <IconComponent
                    className="copy-icon"
                    name={IconName.ExternalLinkIcon}
                  />
                )}
              </Button>
            ) : null}
          </Box>
        ))}
      </Box>
    );
  };

  return isRyanair ? (
    <Slot id="mytrips-ryanair-check-in" />
  ) : (
    <Box className="checkin-flight-modal-content">
      <Box className="checkin-flight-title-container">
        <Typography variant="h4" className={clsx("checkin-flight-title")}>
          {t("checkInModal.title")}
        </Typography>
        <Typography variant="body2" className="checkin-flight-text">
          {t("checkInModal.subtitle")}
        </Typography>
        {isNubankClient ? (
          <Typography variant="body2" className="checkin-flight-info">
            {t("checkInModal.additionalInfo")}
          </Typography>
        ) : null}
        {bookedItinerary.multiTicketType === MultiTicketTypeEnum.HackerFare && (
          <NotificationBanner
            className="status-banner"
            strongLabel={t("checkInModal.notificationHeader")}
            label={t("checkInModal.notificationLabel")}
            severity={BannerSeverity.WARNING}
          />
        )}
      </Box>
      {renderConfirmationNumbers()}
    </Box>
  );
};
